import type { CaseDetail, CaseSummary, ModelPrediction } from '../api/types'

// |p - 0.5| < UNSURE_HALFWIDTH → model is treated as undecided.
// Set conservatively: a model that says 45-55% malignant is essentially flipping
// a coin; calling that side-of-0.5 a "verdict" mis-frames the prediction.
export const UNSURE_HALFWIDTH = 0.1

export type Verdict = 'malignant' | 'benign' | 'unsure'

export interface PredictionView {
  verdict: Verdict
  pMalignant: number
  // 0..1: how far from the decision boundary the model is. 1 = fully confident,
  // 0 = exactly 0.5 (undecided). UI displays this as a percent.
  confidence: number
  // The chosen model name for this cohort.
  modelLabel: string
  // True iff this model was trained on the case's cohort.
  inDistribution: boolean
}

export function pickPrimary(c: CaseSummary | CaseDetail): {
  pMalignant: number
  modelLabel: string
  inDistribution: boolean
} {
  // AMBL cases: prefer v4-v2 (trained on AMBL only, current best AUC).
  // BD cases: prefer v16 (trained on combined AMBL+BD cohorts).
  if (c.cohort === 'BD') {
    return {
      pMalignant: c.p_malignant_v16,
      modelLabel: 'Kombine model',
      inDistribution: true,
    }
  }
  return {
    pMalignant: c.p_malignant_v4v2,
    modelLabel: 'AMBL modeli',
    inDistribution: c.cohort === 'AMBL',
  }
}

export function buildPredictionView(c: CaseSummary | CaseDetail): PredictionView {
  const { pMalignant, modelLabel, inDistribution } = pickPrimary(c)
  const distance = Math.abs(pMalignant - 0.5)
  const confidence = Math.min(1, distance * 2)
  let verdict: Verdict
  if (distance < UNSURE_HALFWIDTH) {
    verdict = 'unsure'
  } else if (pMalignant >= 0.5) {
    verdict = 'malignant'
  } else {
    verdict = 'benign'
  }
  return { verdict, pMalignant, confidence, modelLabel, inDistribution }
}

export type Correctness = 'correct' | 'wrong' | 'borderline' | 'unknown'

export function correctness(c: CaseSummary | CaseDetail, view: PredictionView): Correctness {
  if (c.label === 'Unknown') return 'unknown'
  if (view.verdict === 'unsure') return 'borderline'
  const truthMalignant = c.label === 'Malignant'
  const guessMalignant = view.verdict === 'malignant'
  return truthMalignant === guessMalignant ? 'correct' : 'wrong'
}

export const VERDICT_LABEL: Record<Verdict, string> = {
  malignant: 'Kötü huylu',
  benign: 'İyi huylu',
  unsure: 'Model emin değil',
}

export const CORRECTNESS_LABEL: Record<Correctness, string> = {
  correct: 'Doğru tahmin',
  wrong: 'Yanlış tahmin',
  borderline: 'Sınır vakası',
  unknown: 'Etiket bilinmiyor',
}

export function getDetailModel(detail: CaseDetail): ModelPrediction {
  return detail.cohort === 'BD'
    ? detail.predictions.v16_combined
    : detail.predictions.v4v2_ambl_only
}
